import React from 'react';
import {
  Box,
  Button,
  InputLabel,
  Popover,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers-pro';
import { format } from 'date-fns';
import { useField } from 'formik';
import { cx } from '../../helpers/global';

interface Props {
  name: string;
  label: string;
  disabled: boolean;
  hidden: boolean;
  minDate?: Date;
  displayEmpty?: boolean;
  minutesStep?: number;
}

const DEFAULT_VALUE = null;
const PLACEHOLDER_DATE = '--:-- --';

const BUTTON_SX: SxProps<Theme> = {
  p: 1,
  fontSize: theme => theme.typography.body2.fontSize,
};

function TimeInput({
  name,
  label,
  disabled,
  hidden,
  minDate,
  displayEmpty,
  minutesStep,
}: Props) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLInputElement | null>(null);

  const [{ value }, { error, touched }, { setTouched, setValue }] =
    useField(name);
  const isError = touched && !!error;
  const [tempValue, setTempValue] = React.useState<Date | null>(
    (value ?? DEFAULT_VALUE) as Date | null,
  );

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setTempValue(value ?? DEFAULT_VALUE);
  };

  const onClose = () => {
    setAnchorEl(null);
    setTouched(true);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'date-input-popover' : undefined;

  const onChange = (newDate: Date | null) => {
    setTempValue(newDate ? new Date(newDate) : null);
  };

  const onApply = () => {
    setValue(tempValue);
    setTimeout(() => onClose(), 0);
  };

  return (
    <Box
      className={cx('input', hidden && 'hidden', isError && 'error')}
      position="relative">
      <InputLabel htmlFor={name}>
        <Typography variant="h5">{label}</Typography>
      </InputLabel>
      <input
        id={name}
        name={name}
        onClick={onClick}
        value={`${
          value && !displayEmpty ? format(value, 'hh:mm a') : PLACEHOLDER_DATE
        }`}
        disabled={disabled}
        readOnly
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}>
        <MultiSectionDigitalClock
          value={tempValue}
          onChange={onChange}
          minTime={minDate}
          minutesStep={minutesStep}
          skipDisabled
        />
        <Box
          p={1}
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={1}>
          <Button
            disabled={!tempValue}
            onClick={onApply}
            variant="base"
            color="primary"
            type="button"
            sx={BUTTON_SX}>
            Apply
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}

export default TimeInput;
